import { type SubmitEvent, useEffect, useState } from 'react'
import { type ApiTeamRate, fetchTeamRate, updateTeamRate } from '@/fetcher'
import { SectionCard } from './SectionCard'
import { StatusToast } from './StatusToast'

type TeamRateFormProps = {
  teamId: string
}

const formatRateInput = (value: string) => {
  const normalized = value.replace(',', '.')
  return /^\d+(?:[.,]\d+)?$/.test(normalized) ? normalized : ''
}

const toInputValue = (numeric: string | null) => {
  if (numeric == null) return ''
  const value = Number(numeric)
  return Number.isFinite(value) ? String(value) : ''
}

const parseOptionalPercent = (value: string) => {
  if (!value.trim()) return null

  const normalized = value.replace(',', '.')
  if (!/^\d+(?:\.\d+)?$/.test(normalized)) return undefined

  const percent = Number(normalized)
  return Number.isFinite(percent) ? percent : undefined
}

export const TeamRateForm = ({ teamId }: TeamRateFormProps) => {
  const [team, setTeam] = useState<ApiTeamRate | null>(null)
  const [autoUpdateRate, setAutoUpdateRate] = useState(false)
  const [usdtSellRate, setUsdtSellRate] = useState('')
  const [usdtRateCbrMarkupPercent, setUsdtRateCbrMarkupPercent] = useState('')
  const [usdtRateForexMarkupPercent, setUsdtRateForexMarkupPercent] =
    useState('')
  const [usdtRateSupplierMarkupPercent, setUsdtRateSupplierMarkupPercent] =
    useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [toast, setToast] = useState<{
    message: string
    tone: 'error' | 'success'
  } | null>(null)

  useEffect(() => {
    let isMounted = true

    setIsLoading(true)
    setTeam(null)
    fetchTeamRate(teamId)
      .then((payload) => {
        if (!isMounted) return
        setTeam(payload)
        setAutoUpdateRate(payload.autoUpdateRate)
        setUsdtSellRate(toInputValue(payload.usdtSellRate))
        setUsdtRateCbrMarkupPercent(
          toInputValue(payload.usdtRateCbrMarkupPercent),
        )
        setUsdtRateForexMarkupPercent(
          toInputValue(payload.usdtRateForexMarkupPercent),
        )
        setUsdtRateSupplierMarkupPercent(
          toInputValue(payload.usdtRateSupplierMarkupPercent),
        )
      })
      .catch((error) => {
        if (!isMounted) return
        setToast({
          message:
            error instanceof Error
              ? error.message
              : 'Не удалось загрузить курс компании',
          tone: 'error',
        })
      })
      .finally(() => {
        if (isMounted) setIsLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [teamId])

  const handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault()

    const rateValue = formatRateInput(usdtSellRate)
    const cbrMarkup = parseOptionalPercent(usdtRateCbrMarkupPercent)
    const forexMarkup = parseOptionalPercent(usdtRateForexMarkupPercent)
    const supplierMarkup = parseOptionalPercent(usdtRateSupplierMarkupPercent)

    if (
      !rateValue ||
      cbrMarkup === undefined ||
      forexMarkup === undefined ||
      supplierMarkup === undefined
    ) {
      setToast({
        message: 'Проверьте значение курса и процентов',
        tone: 'error',
      })
      return
    }

    if (
      autoUpdateRate &&
      cbrMarkup == null &&
      forexMarkup == null &&
      supplierMarkup == null
    ) {
      setToast({
        message:
          'Для плавающего курса укажите процент хотя бы для одного источника',
        tone: 'error',
      })
      return
    }

    setIsSaving(true)
    try {
      const updated = await updateTeamRate(teamId, {
        usdtSellRate: Number(rateValue),
        usdtRateCbrMarkupPercent: cbrMarkup,
        usdtRateForexMarkupPercent: forexMarkup,
        usdtRateSupplierMarkupPercent: supplierMarkup,
        autoUpdateRate,
      })
      setTeam(updated)
      setUsdtSellRate(toInputValue(updated.usdtSellRate))
      setUsdtRateCbrMarkupPercent(
        toInputValue(updated.usdtRateCbrMarkupPercent),
      )
      setUsdtRateForexMarkupPercent(
        toInputValue(updated.usdtRateForexMarkupPercent),
      )
      setUsdtRateSupplierMarkupPercent(
        toInputValue(updated.usdtRateSupplierMarkupPercent),
      )
      setToast({ message: 'Курс сохранён', tone: 'success' })
    } catch (error) {
      setToast({
        message:
          error instanceof Error ? error.message : 'Не удалось сохранить курс',
        tone: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="rounded-[22px] bg-[var(--app-card)] px-4 py-6 text-center text-sm text-[var(--app-muted)]">
        Загрузка курса...
      </div>
    )
  }

  if (!team) {
    return null
  }

  return (
    <>
      <form className="space-y-4" onSubmit={handleSubmit}>
        <SectionCard title="Режим курса">
          <div className="flex gap-2">
            <button
              type="button"
              className={`flex-1 rounded-[18px] px-4 py-3 text-sm font-medium transition ${
                autoUpdateRate
                  ? 'bg-[var(--app-accent)] text-[var(--app-accent-text)]'
                  : 'bg-[var(--app-input)] text-[var(--app-muted)]'
              }`}
              onClick={() => setAutoUpdateRate(true)}
            >
              Плавающий
            </button>
            <button
              type="button"
              className={`flex-1 rounded-[18px] px-4 py-3 text-sm font-medium transition ${
                !autoUpdateRate
                  ? 'bg-[var(--app-accent)] text-[var(--app-accent-text)]'
                  : 'bg-[var(--app-input)] text-[var(--app-muted)]'
              }`}
              onClick={() => setAutoUpdateRate(false)}
            >
              Фиксированный
            </button>
          </div>
        </SectionCard>

        <SectionCard
          title="Параметры курса"
          footer={
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex min-h-12 w-full items-center justify-center rounded-full bg-[var(--app-accent)] px-5 py-3 text-sm font-semibold text-[var(--app-accent-text)] transition hover:opacity-90 disabled:opacity-50"
            >
              {isSaving ? 'Сохранение...' : 'Сохранить'}
            </button>
          }
        >
          <div className="space-y-3">
            <div className="space-y-2">
              <label
                className="block text-sm font-medium text-[var(--app-text)]"
                htmlFor="usdt-rate-cbr-markup-percent"
              >
                Наценка от ЦБ (%)
              </label>
              <input
                id="usdt-rate-cbr-markup-percent"
                className="w-full rounded-[14px] bg-[var(--app-input)] px-4 py-3 text-sm text-[var(--app-text)] outline-none focus:ring-2 focus:ring-[var(--app-accent-soft)]"
                inputMode="decimal"
                placeholder="Не использовать"
                value={usdtRateCbrMarkupPercent}
                onChange={(event) =>
                  setUsdtRateCbrMarkupPercent(event.target.value)
                }
              />
            </div>

            <div className="space-y-2">
              <label
                className="block text-sm font-medium text-[var(--app-text)]"
                htmlFor="usdt-rate-forex-markup-percent"
              >
                Наценка от Forex (%)
              </label>
              <input
                id="usdt-rate-forex-markup-percent"
                className="w-full rounded-[14px] bg-[var(--app-input)] px-4 py-3 text-sm text-[var(--app-text)] outline-none focus:ring-2 focus:ring-[var(--app-accent-soft)]"
                inputMode="decimal"
                placeholder="Не использовать"
                value={usdtRateForexMarkupPercent}
                onChange={(event) =>
                  setUsdtRateForexMarkupPercent(event.target.value)
                }
              />
            </div>

            <div className="space-y-2">
              <label
                className="block text-sm font-medium text-[var(--app-text)]"
                htmlFor="usdt-rate-supplier-markup-percent"
              >
                Наценка от поставщика (%)
              </label>
              <input
                id="usdt-rate-supplier-markup-percent"
                className="w-full rounded-[14px] bg-[var(--app-input)] px-4 py-3 text-sm text-[var(--app-text)] outline-none focus:ring-2 focus:ring-[var(--app-accent-soft)]"
                inputMode="decimal"
                placeholder="Не использовать"
                value={usdtRateSupplierMarkupPercent}
                onChange={(event) =>
                  setUsdtRateSupplierMarkupPercent(event.target.value)
                }
              />
              <p className="text-xs text-[var(--app-muted)]">
                Пустое поле отключает источник, 0% использует его курс без
                наценки. Плавающий курс равен максимуму среди доступных
                источников с заданным процентом.
              </p>
            </div>

            {autoUpdateRate ? (
              <p className="text-sm text-[var(--app-muted)]">
                Текущий автоматический курс: {usdtSellRate || '—'} RUB
              </p>
            ) : (
              <div className="space-y-2">
                <label
                  className="block text-sm font-medium text-[var(--app-text)]"
                  htmlFor="usdt-sell-rate"
                >
                  Значение (RUB)
                </label>
                <input
                  id="usdt-sell-rate"
                  className="w-full rounded-[14px] bg-[var(--app-input)] px-4 py-3 text-sm text-[var(--app-text)] outline-none focus:ring-2 focus:ring-[var(--app-accent-soft)]"
                  inputMode="decimal"
                  placeholder="0"
                  value={usdtSellRate}
                  onChange={(event) => setUsdtSellRate(event.target.value)}
                />
              </div>
            )}
          </div>
        </SectionCard>
      </form>

      {toast && (
        <StatusToast
          message={toast.message}
          tone={toast.tone}
          onClose={() => setToast(null)}
        />
      )}
    </>
  )
}
